
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import Banner from "../Components/Banner";

const servicesData = [
  {
    id: 1,
    title: "Databricks Implementation",
    desc: "Implement Databricks solutions tailored to your business with workspace configuration, data pipelines, platform setup, and scalable data processing workflows.",
    link: "/data-ai-databricks-solutions/databricks-consulting",
    image: "/assets/services/img1.png",
  },
  {
    id: 2,
    title: "Lakehouse Architecture & Migration",
    desc: "Build modern lakehouse architectures and migrate legacy data platforms to Databricks for unified data engineering, analytics, and AI workloads.",
    link: "/data-ai-databricks-solutions/lakehouse-architecture",
    image: "/assets/services/img2.png",
  },
  {
    id: 3,
    title: "Data Engineering & Governance",
    desc: "Develop reliable data pipelines and establish data governance, quality controls, security, and access management for trusted enterprise data.",
    link: "/data-ai-databricks-solutions/data-engineering",
    image: "/assets/services/img3.png",
  },
  {
    id: 4,
    title: "GenAI & Agents on Databricks",
    desc: "Build generative AI applications and intelligent agents using enterprise data, retrieval-augmented generation, and Databricks AI capabilities.",
    link: "/data-ai-databricks-solutions/generative-ai-agent-bricks",
    image: "/assets/services/img4.png",
  },
  {
    id: 5,
    title: "Analytics, BI & Dashboards",
    desc: "Transform trusted data into actionable insights through Databricks SQL, business intelligence, interactive dashboards, and performance analytics.",
    link: "/data-ai-databricks-solutions/databricks-sql-bi",
    image: "/assets/services/img5.png",
  },
  {
    id: 6,
    title: "Databricks Managed Services",
    desc: "Keep your Databricks environment reliable and optimized with ongoing platform monitoring, performance tuning, cost optimization, and technical support.",
    link: "/data-ai-databricks-solutions/databricks-managed-services",
    image: "/assets/services/img6.png",
  },
];

const DataAiDatabricks = () => {
  return (
    <>
      <Helmet>
        <title>Data & AI · Databricks Solutions | Mobikode</title>

        <meta
          name="description"
          content="Explore Mobikode's Databricks services, including implementation, lakehouse architecture and migration, data engineering, governance, GenAI, analytics, and managed services."
        />

        <meta
          name="keywords"
          content="Databricks Consulting, Databricks Implementation, Lakehouse Architecture, Databricks Migration, Data Engineering, Data Governance, Generative AI on Databricks, Databricks SQL, BI Dashboards, Databricks Managed Services"
        />

        <meta
          property="og:title"
          content="Data & AI · Databricks Solutions | Mobikode"
        />

        <meta
          property="og:description"
          content="Build trusted data foundations and scalable AI solutions with Databricks implementation, lakehouse architecture, data engineering, analytics, and managed services."
        />

        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Mobikode" />

        <meta
          name="twitter:card"
          content="summary_large_image"
        />

        <meta
          name="twitter:title"
          content="Data & AI · Databricks Solutions | Mobikode"
        />

        <meta
          name="twitter:description"
          content="Discover Databricks implementation, lakehouse migration, data engineering, GenAI, analytics, and managed services from Mobikode."
        />
      </Helmet>

      <div className="bg-[#FFFFFF]">
        {/* Hero Section */}
        <section className="relative w-full h-[500px] lg:h-[630px] flex items-center bg-[#000814] overflow-hidden py-12 md:py-0">
          <div className="absolute inset-0 z-0">
            <img
              src="/assets/services/bg3.png"
              alt="Data engineering, lakehouse architecture, analytics and AI solutions"
              className="w-full h-full object-cover"
            />

            <div
              style={{
                background:
                  "linear-gradient(86.84deg, #000000 20%, rgba(0, 0, 0, 0) 79%)",
              }}
              className="hidden md:block absolute inset-0"
            />

            <div
              style={{
                background:
                  "linear-gradient(0deg, #000000 40%, rgba(0, 0, 0, 0.3) 100%)",
              }}
              className="block md:hidden absolute inset-0"
            />
          </div>

          <div className="relative z-10 w-full max-w-[1440px] mx-auto px-[20px] md:px-[40px] lg:px-[80px] flex flex-col items-start justify-center h-full">
            <div className="w-full max-w-[500px] md:max-w-[700px] lg:max-w-[900px] mt-auto md:mt-0">
              <h1 className="text-white font-[600] text-[28px] sm:text-[36px] md:text-[50px] lg:text-[60px] font-sora leading-[1.2] lg:leading-[1.1] mb-[12px] lg:mb-[22px] drop-shadow-xl tracking-wide text-left">
                Data & AI · Databricks
              </h1>

              <p className="text-[#FFFFFF] max-w-[800px] w-full text-[14px] sm:text-[16px] md:text-[18px] leading-[20px] md:leading-[22px] lg:leading-[24px] font-graphik font-light tracking-wider text-left opacity-90 lg:opacity-100">
                Data you can trust · Partner
              </p>
            </div>
          </div>
        </section>

        {/* Databricks Services */}
        <section className="lg:py-[100px] py-[60px]">
          <div className="max-w-[1440px] w-full mx-auto lg:px-[80px] px-[20px]">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-[20px]">
              {servicesData.map((service) => (
                <ServiceCard key={service.id} service={service} />
              ))}
            </div>
          </div>
        </section>

        {/* Closing Banner */}
        <Banner
          title="Turn Trusted Data Into Business Value"
          text="Build a stronger data foundation with Databricks implementation, lakehouse architecture, intelligent analytics, and AI solutions designed to support business growth."
        />
      </div>
    </>
  );
};

export default DataAiDatabricks;

const ServiceCard = ({ service }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <motion.div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      animate={{ y: isHovered ? -15 : 0 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
    >
      <Link
        to={service.link}
        aria-label={`Explore ${service.title}`}
        className="group outline-none relative block w-full h-[290px] rounded-[0px] overflow-hidden bg-[#ebeff3] transition-colors duration-500 hover:bg-[#01386A] focus:bg-[#01386A] active:bg-[#01386A] cursor-pointer"
      >
        {/* Right Background Image */}
        <div className="absolute top-0 right-[-5%] sm:right-0 w-[45%] sm:w-[40%] h-full pointer-events-none overflow-hidden flex items-center justify-end">
          <img
            src={service.image}
            alt=""
            aria-hidden="true"
            className="w-full h-full object-contain object-right opacity-90 group-hover:opacity-20 group-focus:opacity-20 group-active:opacity-20 transition-all duration-500 transform group-hover:scale-105"
          />
        </div>

        {/* Card Content */}
        <div className="relative z-10 flex flex-col justify-between h-full w-[85%] sm:w-[75%] p-8 lg:p-8">
          <h3 className="text-[16px] lg:text-[20px] font-[600] font-sora text-[#01386A] leading-[1.2] transition-colors duration-500 group-hover:text-white group-focus:text-white group-active:text-white">
            {service.title}
          </h3>

          <p className="absolute top-[105px] sm:top-[115px] pr-5 text-[14px] lg:text-[16px] font-[500] leading-[1.6] font-graphik text-gray-200 opacity-0 translate-y-4 transition-all duration-500 group-hover:opacity-100 group-hover:translate-y-0 group-focus:opacity-100 group-focus:translate-y-0 group-active:opacity-100 group-active:translate-y-0">
            {service.desc}
          </p>

          <div className="mt-auto flex items-center gap-2 text-[15px] font-sora font-[600] text-[#01386A] transition-opacity duration-300 group-hover:opacity-0 group-focus:opacity-0 group-active:opacity-0">
            <span>Read More</span>
            <ArrowUpRight size={18} className="text-[#ED6800]" />
          </div>
        </div>
      </Link>
    </motion.div>
  );
};
